import { t } from "i18next";
import { useEffect, useState } from "react";
import { Button, Form, Modal, Table, Badge } from "react-bootstrap";
import { useKpiSettings } from "../../../contexts/KpiSettingsContext";
import { LoadingSpinner } from "../../general/loading-spinner";
import { KpiDefinition, emptyKpiDefinition } from "../../../interfaces/athlete";
import { saveKpiDefinitions } from "../../../services/kpi";

export function AdminSettings() {
    const { kpiDefinitions, kpiSettingsLoaded, refreshKpiSettings } = useKpiSettings();
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingKpi, setEditingKpi] = useState<KpiDefinition | null>(null);
    const [formData, setFormData] = useState<Partial<KpiDefinition>>(emptyKpiDefinition);

    const handleAddNew = () => {
        setEditingKpi(null);
        setFormData({
            kpi_name: '', // Will be auto-generated on submit
            display_name: '',
            description: '',
            unit: '',
            data_type: 'decimal',
            display_format: '0.00', // Auto-set based on data_type
            weight: kpiDefinitions.length + 1,
            is_active: true
        });
        setShowModal(true);
    };

    const handleEdit = (kpi: KpiDefinition) => {
        setEditingKpi(kpi);
        setFormData(kpi);
        setShowModal(true);
    };

    const handleDelete = async (kpi: KpiDefinition) => {
        if (!window.confirm(t("Are you sure you want to delete this KPI? This action cannot be undone if there is data associated."))) {
            return;
        }

        setLoading(true);
        try {
            // Remove the KPI from the array
            const updatedKpis = kpiDefinitions.filter(k => k.id !== kpi.id);

            // Save the updated array
            const response = await saveKpiDefinitions(updatedKpis);
            if (response.data.status === 1) {
                alert(t("KPI deleted successfully"));
                refreshKpiSettings();
            } else {
                alert(response.data.message || t("Error deleting KPI"));
            }
        } catch (error: any) {
            console.error(error);
            alert(error.response?.data?.message || t("Error deleting KPI. It may be in use."));
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (kpi: KpiDefinition) => {
        setLoading(true);
        try {
            // Update the KPI in the array
            const updatedKpis = kpiDefinitions.map(k =>
                k.id === kpi.id ? { ...k, is_active: !k.is_active } : k
            );

            // Save the updated array
            const response = await saveKpiDefinitions(updatedKpis);
            if (response.data.status === 1) {
                refreshKpiSettings();
            } else {
                alert(t("Error updating KPI"));
            }
        } catch (error) {
            console.error(error);
            alert(t("Error updating KPI"));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true);

        try {
            let updatedKpis: KpiDefinition[];

            if (editingKpi) {
                // Update existing KPI in the array (keep the same kpi_name)
                updatedKpis = kpiDefinitions.map(k =>
                    k.id === editingKpi.id ? { ...k, ...formData, kpi_name: editingKpi.kpi_name } : k
                );
            } else {
                // Generate auto-incremented kpi_name: kpi1, kpi2, kpi3, etc.
                const existingNumbers = kpiDefinitions
                    .map(k => k.kpi_name)
                    .filter(name => /^kpi\d+$/.test(name))
                    .map(name => parseInt(name.replace('kpi', ''), 10))
                    .filter(num => !isNaN(num));

                const nextNumber = existingNumbers.length > 0
                    ? Math.max(...existingNumbers) + 1
                    : 1;

                const autoKpiName = `kpi${nextNumber}`;

                // Add new KPI to the array with a new local ID and auto-generated kpi_name
                const newId = kpiDefinitions.length > 0
                    ? Math.max(...kpiDefinitions.map(k => k.id)) + 1
                    : 1;

                updatedKpis = [
                    ...kpiDefinitions,
                    {
                        ...formData,
                        kpi_name: autoKpiName,
                        id: newId,
                        created_by: 0,
                        created_at: '',
                        changed_by: 0,
                        changed_at: ''
                    } as KpiDefinition
                ];
            }

            // Save the complete array
            const response = await saveKpiDefinitions(updatedKpis);

            if (response.data.status === 1) {
                setShowModal(false);
                refreshKpiSettings();
            } else {
                alert(response.data.message || t("Error saving KPI"));
            }
        } catch (error: any) {
            console.error(error);
            alert(error.response?.data?.message || t("Error saving KPI"));
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field: string, value: any) => {
        const updates: any = { [field]: value };

        // Auto-set display_format based on data_type
        if (field === 'data_type') {
            updates.display_format = value === 'time' ? 'hh:mm:ss.ms' : '0.00';
        }

        setFormData(prev => ({
            ...prev,
            ...updates
        }));
    };

    return (
        <div className="admin-settings-container">
            <h1>{t("Settings")}</h1>

            <div className="settings-section">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2>{t("Performance KPIs Configuration")}</h2>
                        <p className="text-muted">
                            {t("Manage dynamic performance indicators for athletes")}
                        </p>
                    </div>
                    <Button variant="primary" onClick={handleAddNew}>
                        <i className="bi bi-plus-circle me-2"></i>
                        {t("Add KPI")}
                    </Button>
                </div>

                {!kpiSettingsLoaded ? (
                    <LoadingSpinner />
                ) : (
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th style={{ width: '80px' }}>{t("Order")}</th>
                                <th>{t("Name")}</th>
                                <th>{t("Display Name")}</th>
                                <th>{t("Unit")}</th>
                                <th>{t("Type")}</th>
                                <th style={{ width: '100px' }}>{t("Status")}</th>
                                <th style={{ width: '150px' }}>{t("Actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {kpiDefinitions.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="text-center">
                                        {t("No KPIs defined yet. Click 'Add KPI' to create one.")}
                                    </td>
                                </tr>
                            ) : (
                                kpiDefinitions.map(kpi => (
                                    <tr key={kpi.id}>
                                        <td className="text-center">{kpi.weight}</td>
                                        <td><code>{kpi.kpi_name}</code></td>
                                        <td>{kpi.display_name}</td>
                                        <td>{kpi.unit || '-'}</td>
                                        <td className="text-center">
                                            <Badge bg={kpi.data_type === 'time' ? 'info' : 'secondary'}>
                                                {kpi.data_type === 'time' ? t("Duration") : t("Decimal")}
                                            </Badge>
                                        </td>
                                        <td className="text-center">
                                            <Badge bg={kpi.is_active ? 'success' : 'secondary'}>
                                                {kpi.is_active ? t("Active") : t("Inactive")}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    size="sm"
                                                    variant={kpi.is_active ? 'warning' : 'success'}
                                                    onClick={() => handleToggleActive(kpi)}
                                                    disabled={loading}
                                                    title={kpi.is_active ? t("Deactivate") : t("Activate")}
                                                >
                                                    <i className={`bi bi-${kpi.is_active ? 'pause' : 'play'}-fill`}></i>
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="primary"
                                                    onClick={() => handleEdit(kpi)}
                                                    disabled={loading}
                                                    title={t("Edit")}
                                                >
                                                    <i className="bi bi-pencil-square"></i>
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="danger"
                                                    onClick={() => handleDelete(kpi)}
                                                    disabled={loading}
                                                    title={t("Delete")}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                )}
            </div>

            {/* Modal for Add/Edit KPI */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingKpi ? t("Edit KPI") : t("Add New KPI")}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        {editingKpi && (
                            <Form.Group className="mb-3">
                                <Form.Label>{t("KPI Identifier")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.kpi_name || ''}
                                    disabled
                                    readOnly
                                />
                                <Form.Text className="text-muted">
                                    {t("Auto-generated identifier (cannot be changed)")}
                                </Form.Text>
                            </Form.Group>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>{t("Display Name")} *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.display_name || ''}
                                onChange={(e) => handleInputChange('display_name', e.target.value)}
                                placeholder={t("e.g., Speed, Power, Endurance")}
                                required
                            />
                            {!editingKpi && (
                                <Form.Text className="text-muted">
                                    {t("A unique identifier will be generated automatically (kpi1, kpi2, etc.)")}
                                </Form.Text>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>{t("Description")}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={formData.description || ''}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                                placeholder={t("Brief description of what this KPI measures")}
                            />
                        </Form.Group>

                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>{t("Type")} *</Form.Label>
                                    <Form.Select
                                        value={formData.data_type || 'decimal'}
                                        onChange={(e) => handleInputChange('data_type', e.target.value)}
                                        required
                                    >
                                        <option value="decimal">{t("Decimal (2 decimals)")}</option>
                                        <option value="time">{t("Timed Duration (hh:mm:ss.ms)")}</option>
                                    </Form.Select>
                                    <Form.Text className="text-muted">
                                        {formData.data_type === 'time'
                                            ? t("Values stored in seconds, displayed as hh:mm:ss.ms")
                                            : t("Values displayed with 2 decimal places")}
                                    </Form.Text>
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>{t("Unit")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.unit || ''}
                                        onChange={(e) => handleInputChange('unit', e.target.value)}
                                        placeholder={t("e.g., m/s, W, kg, %")}
                                    />
                                    <Form.Text className="text-muted">
                                        {t("Optional unit of measurement")}
                                    </Form.Text>
                                </Form.Group>
                            </div>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label>{t("Display Order")}</Form.Label>
                            <Form.Control
                                type="number"
                                value={formData.weight || 1}
                                onChange={(e) => handleInputChange('weight', parseInt(e.target.value))}
                                min="1"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label={t("Active (visible to users)")}
                                checked={formData.is_active ?? true}
                                onChange={(e) => handleInputChange('is_active', e.target.checked)}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)} disabled={loading}>
                            {t("Cancel")}
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? t("Saving...") : t("Save")}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}
